import React from 'react';

export interface Props {
    render: (
        props: State &
            Pick<
                AsyncRender,
                'load' | 'handleProgress' | 'cancel' | 'cancelController' | 'hideErrorMessage'
            >
    ) => React.ReactNode;
    onLoad: (...params: any[]) => Promise<any>;
    onSuccess: (res: any) => void;
    onError?: (error: any) => void;
}

type State = {
    error: null | Error;
    inProgress: boolean;
    progressValue: null | number;
};

class AsyncRender extends React.PureComponent<Props, State> {
    cancelController = { cancel: (message?: string) => {} };

    state = {
        error: null,
        inProgress: false,
        progressValue: null,
    };

    load = (...params: any[]) => {
        const { inProgress } = this.state;

        if (inProgress) return;

        this.setState(
            {
                error: null,
                inProgress: true,
                progressValue: 0,
            },
            () => {
                const promise = this.props.onLoad(...params);
                if (!promise || !promise.then || !promise.catch) {
                    throw Error("onLoad handler doesn't return a promise");
                }
                promise.then(this.handleSuccess).catch(this.handleError);
            }
        );
    };

    cancel = (message?: string) => {
        this.setState({
            inProgress: false,
        });
        this.cancelController.cancel(message);
    };

    handleProgress = (pe: ProgressEvent) => {
        const { total, loaded, lengthComputable } = pe;

        const progressValue = lengthComputable ? loaded / total : loaded;
        this.setState({ progressValue });
    };

    handleSuccess = (res: any) => {
        const { onSuccess } = this.props;
        onSuccess(res);

        setTimeout(
            () =>
                this.setState({
                    inProgress: false,
                }),
            500
        );
    };

    hideErrorMessage = () => {
        this.setState({
            error: null,
        });
    };
    handleError = (error: Error) => {
        if (error.constructor.name !== 'Cancel') {
            this.setState({
                error: error,
            });
        }

        this.setState({
            inProgress: false,
        });

        const { onError } = this.props;
        if (onError) {
            onError(error);
        }
    };

    render() {
        const { inProgress, progressValue, error } = this.state;
        const { handleProgress, load, cancel, cancelController, hideErrorMessage } = this;

        return this.props.render({
            error,
            inProgress,
            progressValue,
            handleProgress,
            load,
            cancel,
            cancelController,
            hideErrorMessage,
        });
    }
}

export default AsyncRender;
